import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { isoToEU } from "../../lib/format";
import {
  TrendingUp, Calendar, CarFront, Users, Activity, ArrowRight, ArrowDown, ArrowUp, Mail, Printer,
} from "lucide-react";
import EnvironmentHealthPanel from "../../components/admin/EnvironmentHealthPanel";

const PERIODS = [
  { id: "today", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "all_time", label: "All-time" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("week");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/stats")
      .then((r) => {
        if (cancelled) return;
        const d = r?.data;
        setStats(
          d && typeof d === "object" && !Array.isArray(d) && d.counts && d.earnings
            ? d
            : null,
        );
      })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const last14 = Array.isArray(stats?.revenue_last_14_days) ? stats.revenue_last_14_days : [];
  const { revenueTotal14d, maxRev } = useMemo(() => ({
    revenueTotal14d: last14.reduce((s, d) => s + (Number(d?.total) || 0), 0),
    maxRev: Math.max(1, ...last14.map((d) => Number(d?.total) || 0)),
  }), [last14]);

  if (loading) {
    return <p className="text-neutral-500 dark:text-neutral-400">Loading dashboard…</p>;
  }
  if (!stats) {
    return <p className="text-neutral-500 dark:text-neutral-400">Unable to load stats.</p>;
  }

  const earnings = stats.earnings[period] || 0;

  return (
    <div data-testid="admin-dashboard">
      {/* Heading */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Overview</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Operations <em className="italic">at a glance</em>.
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SendBriefButton />
          <div className="flex gap-1 bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-1 rounded">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  period === p.id
                    ? "bg-black text-white dark:bg-white dark:text-black"
                    : "text-neutral-600 dark:text-neutral-400 hover:bg-black/5 dark:hover:bg-white/5"
                }`}
                data-testid={`period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* v4.6.15 — top row: Earnings back in slot 1, then the three
          operational metrics (two clickable through to the day's manifest). */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
        <KpiCard
          icon={<TrendingUp size={16} />}
          label={`Earnings · ${PERIODS.find(p => p.id === period).label}`}
          value={`€${earnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          accent
          testId="kpi-earnings"
        />
        <OpsCard
          icon={<ArrowUp size={16} />}
          label="Pickups today"
          value={stats.counts.pickups_today}
          sub={`${stats.counts.pickups_week} this week`}
          to="/admin/bookings?filter=pickups_today"
          testId="kpi-pickups-today"
        />
        <OpsCard
          icon={<ArrowDown size={16} />}
          label="Returns today"
          value={stats.counts.returns_today}
          sub="Due back at the desk"
          to="/admin/bookings?filter=returns_today"
          testId="kpi-returns-today"
        />
        <OpsCard
          icon={<Activity size={16} />}
          label="Active rentals"
          value={stats.counts.active_rentals}
          sub={`${stats.counts.total_vehicles} vehicles in fleet`}
          testId="kpi-active"
        />
      </div>

      {/* Agenda sits directly under the operational cards. */}
      <div className="mb-8">
        <AgendaTabs />
      </div>

      {/* Revenue chart + top vehicles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-6 rounded">
          <div className="flex justify-between items-start mb-6">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Revenue</p>
              <h2 className="font-display text-2xl mt-1">Last 14 days</h2>
            </div>
            <p className="font-display text-2xl">
              €{revenueTotal14d.toFixed(2)}
            </p>
          </div>
          <div className="flex items-end gap-1.5 h-40" data-testid="revenue-chart">
            {last14.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5 group">
                <div
                  className="w-full bg-[#0A0A0A] dark:bg-[#E9E7E1] hover:bg-[#004B35] dark:hover:bg-[#7CFFA8] transition-colors rounded-t"
                  style={{ height: `${(d.total / maxRev) * 100}%`, minHeight: d.total > 0 ? "2px" : "0px" }}
                  title={`${d.date} · €${d.total.toFixed(2)}`}
                />
                <span className="text-[9px] text-neutral-400 dark:text-neutral-500 hidden md:block">
                  {d.date.slice(5)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-6 rounded">
          <div className="flex justify-between items-start mb-6">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Top Performers</p>
              <h2 className="font-display text-2xl mt-1">Vehicles</h2>
            </div>
            <CarFront size={18} className="text-neutral-400" />
          </div>
          {(Array.isArray(stats.top_vehicles) ? stats.top_vehicles : []).length === 0 && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">No paid bookings yet.</p>
          )}
          <ul className="space-y-3" data-testid="top-vehicles">
            {(Array.isArray(stats.top_vehicles) ? stats.top_vehicles : []).map((v, i) => (
              <li key={v.name} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-display text-lg text-neutral-300 dark:text-neutral-600 w-5">{i + 1}</span>
                  <span className="text-sm truncate">{v.name}</span>
                </div>
                <span className="text-sm font-medium tabular-nums">€{v.revenue.toFixed(0)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* v4.6.15 — secondary row: payment methods regains its wider footprint
          now that earnings sits back in the top row. */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <PaymentMethodBreakdown methods={stats.earnings_by_method || {}} />
        </div>
        <CustomersCard counts={stats.counts} />
      </div>

      {/* Voucher analytics */}
      {stats.voucher_analytics && (
        <div className="mt-5">
          <VoucherAnalyticsPanel data={stats.voucher_analytics} />
        </div>
      )}

      {/* Environment health — integrations & config readiness */}
      <div className="mt-5">
        <EnvironmentHealthPanel />
      </div>

      {/* Quick links */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
        <QuickLink to="/admin/bookings" label="Manage Bookings" desc={`${stats.counts.pickups_today} pickups due today`} testId="quick-bookings" />
        <QuickLink to="/admin/fleet" label="Fleet Settings" desc={`${stats.counts.total_vehicles} vehicles in fleet`} testId="quick-fleet" />
        <QuickLink to="/admin/locations" label="Locations" desc="Pickup spots & hours" testId="quick-locations" />
        <QuickLink to="/admin/vouchers" label="Vouchers" desc={`${stats.voucher_analytics?.redemptions ?? 0} redemptions to date`} testId="quick-vouchers" />
        <QuickLink to="/admin/business" label="Business Policies" desc="Deposits, taxes, mileage" testId="quick-business" />
      </div>
    </div>
  );
}

function VoucherAnalyticsPanel({ data }) {
  const top = Array.isArray(data?.top_codes) ? data.top_codes : [];
  const maxRed = Math.max(1, ...top.map((c) => Number(c?.redemptions) || 0));
  return (
    <div
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-6 rounded"
      data-testid="voucher-analytics-panel"
    >
      <div className="flex justify-between items-start mb-6">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Promotions</p>
          <h2 className="font-display text-2xl mt-1">Voucher impact</h2>
        </div>
        <Link
          to="/admin/vouchers"
          className="text-[11px] tracking-widest uppercase border-b border-black/30 dark:border-white/30 hover:border-black dark:hover:border-white pb-0.5"
          data-testid="voucher-analytics-cta"
        >
          Manage codes →
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <SubKpi label="Redemptions" value={data.redemptions} testId="voucher-redemptions" />
        <SubKpi
          label="Discount given"
          value={`${Number(data.total_discount_given || 0).toFixed(2)}`}
          testId="voucher-discount-total"
        />
        <SubKpi
          label="Revenue from promo bookings"
          value={`${Number(data.revenue_with_voucher || 0).toFixed(2)}`}
          testId="voucher-revenue"
        />
      </div>

      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 mb-3">Top codes</p>
        {top.length === 0 && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No voucher redemptions yet. Create your first promo.</p>
        )}
        {top.length > 0 && (
          <ul className="space-y-3" data-testid="top-vouchers-list">
            {top.map((c) => (
              <li key={c.code} className="grid grid-cols-12 gap-3 items-center">
                <span className="col-span-3 md:col-span-2 font-mono text-sm tracking-wider truncate">{c.code}</span>
                <div className="col-span-5 md:col-span-7 h-2 bg-black/5 dark:bg-white/10 rounded overflow-hidden">
                  <div
                    className="h-full bg-emerald-600 dark:bg-emerald-500"
                    style={{ width: `${(c.redemptions / maxRed) * 100}%` }}
                  />
                </div>
                <span className="col-span-2 md:col-span-1 text-xs tabular-nums text-right">
                  {c.redemptions}×
                </span>
                <span className="col-span-2 text-xs tabular-nums text-right text-neutral-500 dark:text-neutral-400">
                  €{c.revenue.toFixed(0)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SubKpi({ label, value, testId }) {
  return (
    <div className="p-4 rounded border border-black/8 dark:border-white/10 bg-[#FAF9F6] dark:bg-[#111827]" data-testid={testId}>
      <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="font-display text-2xl md:text-3xl tracking-tight mt-2 tabular-nums">{value}</p>
    </div>
  );
}

const METHOD_LABELS = {
  stripe: "Stripe",
  cash: "Cash",
  bank_transfer: "Bank Transfer",
  card_on_office: "Card on Office",
  paypal: "PayPal",
  unknown: "Other",
};
const METHOD_COLORS = {
  stripe: "#635BFF",
  cash: "#16A34A",
  bank_transfer: "#0EA5E9",
  card_on_office: "#F59E0B",
  paypal: "#1D4ED8",
  unknown: "#A3A3A3",
};

function PaymentMethodBreakdown({ methods }) {
  const safe = methods && typeof methods === "object" && !Array.isArray(methods) ? methods : {};
  const entries = Object.entries(safe).filter(([, v]) => Number(v) > 0);
  const total = entries.reduce((s, [, v]) => s + Number(v || 0), 0);
  return (
    <div className="h-full bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-6 rounded" data-testid="earnings-by-method">
      <div className="flex justify-between items-start mb-6">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Earnings by</p>
          <h2 className="font-display text-2xl mt-1">Payment methods</h2>
        </div>
        <p className="font-display text-xl tabular-nums">€{total.toFixed(2)}</p>
      </div>
      {entries.length === 0 && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">No paid bookings yet.</p>
      )}
      {entries.length > 0 && (
        <>
          {/* Stacked bar */}
          <div className="flex w-full h-3 rounded overflow-hidden">
            {entries.map(([k, v]) => (
              <div
                key={k}
                style={{ width: `${(v / total) * 100}%`, backgroundColor: METHOD_COLORS[k] || "#999" }}
                title={`${METHOD_LABELS[k] || k}: ${v.toFixed(2)}`}
              />
            ))}
          </div>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5" data-testid="method-legend">
            {entries.map(([k, v]) => (
              <li key={k} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: METHOD_COLORS[k] || "#999" }} />
                  <span className="text-sm truncate">{METHOD_LABELS[k] || k}</span>
                </span>
                <span className="text-sm tabular-nums">€{v.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function OpsCard({ icon, label, value, sub, to, testId }) {
  const body = (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400">{label}</span>
        <span className="text-neutral-400">{icon}</span>
      </div>
      <p className="font-display text-3xl md:text-4xl tracking-tight tabular-nums">{value}</p>
      {sub && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{sub}</p>}
    </>
  );
  const shell = "p-5 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-[#1F2937] transition-colors block";
  if (!to) return <div className={shell} data-testid={testId}>{body}</div>;
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`${shell} hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer`}
    >
      {body}
      <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400">
        Open manifest <ArrowRight size={11} />
      </span>
    </Link>
  );
}

function CustomersCard({ counts }) {
  const c = counts || {};
  return (
    <div
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-6 rounded flex flex-col"
      data-testid="customers-card"
    >
      <div className="flex justify-between items-start mb-5">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Clients</p>
          <h2 className="font-display text-2xl mt-1">Customers</h2>
        </div>
        <Users size={18} className="text-neutral-400" />
      </div>
      <p className="font-display text-4xl tracking-tight tabular-nums">{c.total_customers ?? 0}</p>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Registered clients</p>
      <dl className="mt-5 space-y-2 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-neutral-500 dark:text-neutral-400">Total bookings</dt>
          <dd className="font-semibold tabular-nums">{c.total_bookings ?? 0}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-neutral-500 dark:text-neutral-400">Active rentals</dt>
          <dd className="font-semibold tabular-nums">{c.active_rentals ?? 0}</dd>
        </div>
      </dl>
      <Link
        to="/admin/clients"
        className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-semibold hover:gap-2.5 transition-all"
        data-testid="customers-card-cta"
      >
        Open CRM <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, accent, testId }) {
  return (
    <div
      className={`p-5 rounded border transition-all ${
        accent
          ? "bg-[#0A0A0A] text-[#F6F5F2] border-[#0A0A0A] dark:bg-[#E9E7E1] dark:text-[#0A0A0A] dark:border-[#E9E7E1]"
          : "bg-white dark:bg-[#1F2937] border-black/10 dark:border-white/10"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] tracking-[0.25em] uppercase opacity-70">{label}</span>
        <span className="opacity-60">{icon}</span>
      </div>
      <p className="font-display text-3xl md:text-4xl tracking-tight">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-1">{sub}</p>}
    </div>
  );
}

function QuickLink({ to, label, desc, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group p-5 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-[#1F2937] hover:border-black dark:hover:border-white transition-colors flex items-center justify-between"
    >
      <div>
        <p className="font-display text-xl">{label}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{desc}</p>
      </div>
      <ArrowRight size={16} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
    </Link>
  );
}

const AGENDA_TABS = [
  { id: "tomorrow", label: "Tomorrow", days: 1 },
  { id: "next3",    label: "Next 3 Days", days: 3 },
  { id: "next7",    label: "Next 7 Days", days: 7 },
];

function AgendaTabs() {
  const [tab, setTab] = useState("tomorrow");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const activeTab = AGENDA_TABS.find((t) => t.id === tab) || AGENDA_TABS[0];
  const days = activeTab.days;

  // v4.6.16 — print only the agenda sheet: the body flag lets the print
  // stylesheet blank the rest of the dashboard while this sheet stays visible.
  const printAgenda = () => {
    const clear = () => document.body.classList.remove("printing-agenda");
    document.body.classList.add("printing-agenda");
    window.addEventListener("afterprint", clear, { once: true });
    window.print();
    setTimeout(clear, 4000);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/admin/agenda/range", { params: { days } })
      .then((r) => {
        if (cancelled) return;
        const d = r?.data;
        setData(d && typeof d === "object" && !Array.isArray(d) ? d : null);
      })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const pickups = Array.isArray(data?.pickups) ? data.pickups : [];
  const returns = Array.isArray(data?.returns) ? data.returns : [];

  return (
    <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded p-6" data-testid="agenda-card">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Operations</p>
          <h2 className="font-display text-2xl mt-1">Agenda</h2>
          {data?.from && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1" data-testid="agenda-window">
              {isoToEU(data.from)}{data.to !== data.from ? ` → ${isoToEU(data.to)}` : ""}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-sky-600 text-white text-[11px] font-bold uppercase tracking-wider" data-testid="agenda-badge-pickups">
            ↗ {pickups.length} Pickups
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-rose-600 text-white text-[11px] font-bold uppercase tracking-wider" data-testid="agenda-badge-returns">
            ↙ {returns.length} Returns
          </span>
          <div className="flex gap-1 bg-[#FAF9F6] dark:bg-[#111827] border border-black/10 dark:border-white/10 p-1 rounded">
            {AGENDA_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${tab === t.id ? "bg-black text-white dark:bg-white dark:text-black" : "text-neutral-600 dark:text-neutral-400 hover:bg-black/5 dark:hover:bg-white/5"}`}
                data-testid={`agenda-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={printAgenda}
            className="px-3 py-1.5 text-xs rounded border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white transition-colors inline-flex items-center gap-1.5"
            data-testid="agenda-print"
          >
            <Printer size={12} /> Print Agenda
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <AgendaColumn
          title="Pickups"
          icon={<ArrowUp size={14} className="text-sky-600" />}
          items={pickups}
          loading={loading}
          timeField="pickup_time"
          dateField="pickup_date"
          locField="pickup_location"
          testId="agenda-pickups"
        />
        <AgendaColumn
          title="Returns"
          icon={<ArrowDown size={14} className="text-rose-600" />}
          items={returns}
          loading={loading}
          timeField="return_time"
          dateField="return_date"
          locField="return_location"
          testId="agenda-returns"
        />
      </div>

      {/* v4.6.16 — printable sheet for the selected range (hidden on screen). */}
      <div className="agenda-print-sheet hidden" data-testid="agenda-print-sheet">
        <h1 className="agenda-print-title">Operations Agenda · {activeTab.label}</h1>
        <p className="agenda-print-window">
          {data?.from ? `${isoToEU(data.from)}${data.to !== data.from ? ` → ${isoToEU(data.to)}` : ""}` : ""}
          {` · ${pickups.length} pickups · ${returns.length} returns`}
        </p>
        <AgendaPrintTable
          title="Pickups"
          items={pickups}
          dateField="pickup_date"
          timeField="pickup_time"
          locField="pickup_location"
        />
        <AgendaPrintTable
          title="Returns"
          items={returns}
          dateField="return_date"
          timeField="return_time"
          locField="return_location"
        />
      </div>
    </div>
  );
}

function AgendaPrintTable({ title, items, dateField, timeField, locField }) {
  const rows = Array.isArray(items) ? items : [];
  return (
    <section className="agenda-print-section">
      <h2 className="agenda-print-subtitle">{title} ({rows.length})</h2>
      <table>
        <thead>
          <tr>
            <th>Date</th><th>Time</th><th>Reference</th><th>Driver</th>
            <th>Vehicle</th><th>Location</th><th>Phone</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={7}>Nothing scheduled.</td></tr>
          )}
          {rows.map((b) => (
            <tr key={b.id}>
              <td>{isoToEU(b[dateField])}</td>
              <td>{b[timeField] || "—"}</td>
              <td>{(b.booking_reference || b.id || "").slice(0, 8).toUpperCase()}</td>
              <td>{b.full_name || "—"}</td>
              <td>{b.vehicle_snapshot?.name || "—"}</td>
              <td>{b[locField] || "—"}</td>
              <td>{b.phone || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function AgendaColumn({ title, icon, items, loading, timeField, dateField, locField, testId }) {
  const safeItems = Array.isArray(items) ? items : [];
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 flex items-center gap-1.5">
          {icon} {title}
        </span>
        <span className="text-[10px] text-neutral-400">{safeItems.length}</span>
      </div>
      {loading && <p className="text-xs text-neutral-500 dark:text-neutral-400">Loading…</p>}
      {!loading && safeItems.length === 0 && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 italic">Nothing scheduled.</p>
      )}
      <ul className="space-y-2">
        {safeItems.map((b) => (
          <li key={b.id} className="flex items-center justify-between gap-3 border border-black/8 dark:border-white/10 rounded px-3 py-2.5 text-sm">
            <div className="min-w-0">
              <div className="font-medium truncate">{b.vehicle_snapshot?.name || "—"}</div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{b.full_name} · {b[locField]}</div>
            </div>
            <span className="text-right whitespace-nowrap">
              <span className="block text-xs font-semibold tabular-nums">{isoToEU(b[dateField])}</span>
              <span className="block text-[11px] font-mono text-neutral-500 dark:text-neutral-400">{b[timeField] || "—"}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}



function SendBriefButton() {
  const [state, setState] = useState("idle"); // idle, sending, sent, mocked, error
  const [error, setError] = useState("");

  const handle = async () => {
    setState("sending");
    setError("");
    try {
      const { data } = await api.post("/admin/ops/daily-brief");
      if (data.sent) setState("sent");
      else if (data.mock) setState("mocked");
      else { setState("error"); setError(data.error || "Unknown error"); }
      setTimeout(() => setState("idle"), 4000);
    } catch (e) {
      setState("error");
      setError(e.response?.data?.detail || e.message);
    }
  };

  const label = {
    idle: "Send daily brief",
    sending: "Sending…",
    sent: "Brief sent ✓",
    mocked: "Mocked (set RESEND_API_KEY)",
    error: error || "Failed",
  }[state];

  return (
    <button
      onClick={handle}
      disabled={state === "sending"}
      title="Email today's pickups, returns, yesterday's revenue & active rentals to all admin+staff"
      className={`px-3 py-1.5 text-xs rounded border inline-flex items-center gap-1.5 transition-colors ${state === "sent" ? "border-emerald-500 text-emerald-700 dark:text-emerald-400" : state === "error" ? "border-rose-400 text-rose-600 dark:text-rose-400" : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white"}`}
      data-testid="send-daily-brief"
    >
      <Mail size={12} /> {label}
    </button>
  );
}
